import {
  Dialog,
  FoundationColorTokens,
  Spacing,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { TFunction } from "i18next";
import React, { useEffect } from "react";
import {
  AddMultipleSPoCsDialogState,
  AddMultipleSPoCsStore,
} from "../../store/AddMultipleSPoCsStore";
import { Instance } from "mobx-state-tree";
import { DialogSuccessContent } from "../../../../components/DialogSuccessContent";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import { NavigateToFunctions } from "../../../../routes/RoutesHelper";
import { AddMultipleSPoCsDialogChild } from "./AddMultipleSPoCsDialogChild";
import { ValidateCSVAndUploadSpocsRPC } from "@pulse/pulse-rpcs/lib/spoc-management/validateCsvAndUploadSpocsRPC";
import { observer } from "mobx-react";

interface AddMultipleSPoCsDialogProps {
  isAddMultipleSPoCsDialogOpen: boolean;
  setIsAddMultipleSPoCsDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  t: TFunction;
  addMultipleSPoCsStore: Instance<typeof AddMultipleSPoCsStore>;
  navigateTo: NavigateToFunctions;
  spacing: Spacing;
  tokens: FoundationColorTokens<string>;
  typography: Typography;
  onClose: () => Promise<void>;
}

export const AddMultipleSPoCsDialog = observer(
  ({
    isAddMultipleSPoCsDialogOpen,
    setIsAddMultipleSPoCsDialogOpen,
    t,
    addMultipleSPoCsStore,
    navigateTo,
    spacing,
    tokens,
    typography,
    onClose,
  }: AddMultipleSPoCsDialogProps): React.ReactElement => {
    useEffect(() => {
      addMultipleSPoCsStore.resetStore();
    }, []);
    const getSecondaryButtonText = (): string => {
      switch (addMultipleSPoCsStore.addMultipleSPoCsDialogState) {
        case AddMultipleSPoCsDialogState.AddMultipleSPoCs:
        case AddMultipleSPoCsDialogState.ValidationErrorFound: {
          return t("common.cancel");
        }
        case AddMultipleSPoCsDialogState.Error: {
          return t("common.close");
        }
        case AddMultipleSPoCsDialogState.FileUploaded: {
          return t("common.done");
        }
      }
    };
    const getErrorMessage = (): string => {
      switch (addMultipleSPoCsStore.rpcError) {
        case ValidateCSVAndUploadSpocsRPC.RPCError.ProjectAlreadyArchived: {
          return t("common.projectAlreadyArchivedErrorText");
        }
        default: {
          return t("common.fileUploadErrorMessage.uploadErrorMessageText");
        }
      }
    };

    const getDialogChild = (): React.ReactElement => {
      switch (addMultipleSPoCsStore.addMultipleSPoCsDialogState) {
        case AddMultipleSPoCsDialogState.AddMultipleSPoCs:
        case AddMultipleSPoCsDialogState.ValidationErrorFound: {
          return (
            <AddMultipleSPoCsDialogChild
              addMultipleSPoCsStore={addMultipleSPoCsStore}
              navigateTo={navigateTo}
              t={t}
              spacing={spacing}
              typography={typography}
              tokens={tokens}
            />
          );
        }
        case AddMultipleSPoCsDialogState.FileUploaded: {
          return (
            <DialogSuccessContent
              description={t(
                "projects.userAccess.addMultipleSPoCsDialog.successText",
              )}
              spacing={spacing}
              tokens={tokens}
              typography={typography}
            />
          );
        }
        case AddMultipleSPoCsDialogState.Error: {
          return (
            <DialogErrorContent
              errorText={getErrorMessage()}
              spacing={spacing}
              tokens={tokens}
              typography={typography}
            />
          );
        }
      }
    };

    return (
      <Dialog
        open={isAddMultipleSPoCsDialogOpen}
        disableBackdropClick={true}
        isSecondaryButtonDisabled={addMultipleSPoCsStore.isRPCLoading}
        onSecondaryButtonClick={() => {
          onClose();
          setIsAddMultipleSPoCsDialogOpen(false);
        }}
        secondaryButtonText={getSecondaryButtonText()}
        title={t("projects.userAccess.addMultipleSPoCsDialog.title")}
        width="560px"
      >
        {getDialogChild()}
      </Dialog>
    );
  },
);
